import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Truck, ShieldCheck, RefreshCw, MapPin } from "lucide-react";
import { Reveal, MaskedLines } from "../components/Reveal";
import Marquee from "../components/Marquee";
import BundleSection from "../components/BundleSection";
import ParallaxImage from "../components/ParallaxImage";
import { api, PRODUCTS, LIFESTYLE, money } from "../lib/data";

const CHAPTERS = [
  { n: "01", title: "The morning walk", body: "The same loop around the block, done a thousand times, never quite the same. Gear that disappears into the routine." },
  { n: "02", title: "The detour", body: "The trail you didn't plan to take. Mud, water, a longer way home. Built to keep up when the day changes its mind." },
  { n: "03", title: "The long drive", body: "Windows down, a new place at the end of it. A dog beside you is the best kind of co-pilot." },
  { n: "04", title: "The way home", body: "Wherever you end up, there's a way back. A name, a number, a collar that knows the answer." },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.45, 0.7]);

  return (
    <section ref={ref} className="relative h-[100svh] w-full overflow-hidden" data-testid="hero">
      <motion.div style={{ y }} className="absolute inset-0 -z-10 h-[122%]">
        <motion.img
          src={LIFESTYLE.hero}
          alt="A person walking their dog at sunrise"
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2, ease: [0.22, 1, 0.36, 1] }}
          className="lifestyle-img h-full w-full object-cover"
        />
        <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-charcoal" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-charcoal/30" />
      </motion.div>

      <div className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-end px-5 pb-20 md:px-10 md:pb-28">
        <motion.p
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mb-6 font-manrope text-xs uppercase tracking-[0.2em] text-cream/80"
        >
          Kindred Mile — Every mile, together
        </motion.p>
        <h1 className="max-w-4xl font-fraunces text-[13vw] font-light leading-[0.95] text-cream md:text-[6rem]">
          <MaskedLines lines={["Made for the miles", "you share."]} delay={0.32} />
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-7 max-w-lg font-manrope text-lg text-cream/85"
        >
          Gear for walks, detours, muddy trails, and wherever you two end up next.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-9"
        >
          <Link
            to="/shop"
            data-testid="hero-cta-button"
            className="group inline-flex items-center gap-3 rounded-full bg-rust px-8 py-4 font-manrope font-semibold text-cream transition-colors hover:bg-cream hover:text-charcoal"
          >
            Explore the gear <ArrowRight className="hover-arrow h-5 w-5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function Featured({ products }) {
  return (
    <section className="mx-auto max-w-[1400px] px-5 pt-24 pb-10 md:px-10 md:pt-32 md:pb-14" data-testid="featured-products">
      <div className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <Reveal>
          <p className="font-manrope text-xs uppercase tracking-[0.15em] font-bold text-rust">The gear</p>
          <h2 className="mt-4 max-w-xl font-fraunces text-4xl leading-tight md:text-6xl">
            Built for the way you wander.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Link to="/shop" className="group inline-flex items-center gap-2 font-manrope font-semibold hover:text-rust" data-testid="featured-shop-all">
            Shop all <ArrowRight className="hover-arrow h-4 w-4" />
          </Link>
        </Reveal>
      </div>

      <div className="grid gap-8 sm:grid-cols-3">
        {products.map((p, i) => (
          <Reveal key={p.slug} delay={i * 0.08}>
            <Link to={`/products/${p.slug}`} className="group block" data-testid={`product-card-${p.slug}`}>
              <div className="overflow-hidden bg-white ring-1 ring-charcoal/10">
                <img
                  src={p.showcaseImage || p.gallery[0]}
                  alt={p.name}
                  className="img-zoom aspect-square w-full object-contain p-6"
                />
              </div>
              <div className="mt-5 flex items-center justify-between gap-3">
                <div>
                  <p className="font-manrope text-xs uppercase tracking-[0.15em] text-rust">{p.tagline}</p>
                  <h3 className="mt-1 font-fraunces text-xl md:text-2xl">{p.name.split("+")[0].trim()}</h3>
                </div>
                <span className="whitespace-nowrap font-manrope text-lg font-semibold">{money(p.price, p.currency)}</span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section className="bg-forest text-cream" data-testid="manifesto">
      <div className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-32">
        <div className="grid gap-14 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="md:sticky md:top-28">
              <p className="font-manrope text-xs uppercase tracking-[0.15em] font-bold text-rust">Our philosophy</p>
              <h2 className="mt-5 font-fraunces text-4xl leading-tight md:text-5xl">
                A dog isn't an accessory. They're the reason you go the distance.
              </h2>
              <div className="mt-8 overflow-hidden">
                <img src={LIFESTYLE.forestPath} alt="A dog on a sunlit forest trail" className="lifestyle-img aspect-[4/5] w-full object-cover" />
              </div>
              <Link to="/our-story" className="group mt-8 inline-flex items-center gap-2 font-manrope font-semibold text-cream hover:text-rust" data-testid="manifesto-story-link">
                Read our story <ArrowRight className="hover-arrow h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="md:col-span-6 md:col-start-7">
            <div className="flex flex-col gap-16 md:gap-28 md:pt-16">
              {CHAPTERS.map((c) => (
                <Reveal key={c.n} className="border-t border-cream/15 pt-6">
                  <span className="font-fraunces text-xl text-rust">{c.n}</span>
                  <h3 className="mt-3 font-fraunces text-3xl md:text-4xl">{c.title}</h3>
                  <p className="mt-4 max-w-md font-manrope text-lg text-cream/70">{c.body}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SafetyStory() {
  return (
    <section className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-32" data-testid="safety-story">
      <div className="grid items-center gap-12 md:grid-cols-2">
        <Reveal>
          <div className="relative aspect-[4/5] overflow-hidden">
            <ParallaxImage src={LIFESTYLE.roadTrip} alt="A dog on a road trip" className="lifestyle-img" />
          </div>
        </Reveal>
        <Reveal delay={0.1} className="md:pl-10">
          <p className="font-manrope text-xs uppercase tracking-[0.15em] font-bold text-rust">Always Find Home</p>
          <h2 className="mt-4 font-fraunces text-4xl leading-tight md:text-5xl">
            The shortest way home is a stranger who knows where to call.
          </h2>
          <p className="mt-6 font-manrope text-lg text-ink">
            Our Personalized Collar is engraved with your dog's name and your number — no jingling tags to lose. Padded faux-leather, built to soften with every mile.
          </p>
          <Link
            to="/products/personalized-dog-collar"
            data-testid="safety-cta"
            className="group mt-8 inline-flex items-center gap-3 rounded-full border border-charcoal px-8 py-4 font-manrope font-semibold transition-colors hover:bg-charcoal hover:text-cream"
          >
            Personalize a collar <ArrowRight className="hover-arrow h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function Trust() {
  const items = [
    { icon: Truck, title: "Free shipping in Canada", body: "On every order, no minimum." },
    { icon: RefreshCw, title: "30-day returns", body: "Didn't fit the routine? Send it back." },
    { icon: ShieldCheck, title: "Made to last", body: "Materials chosen for the long haul." },
    { icon: MapPin, title: "Ships in 1–2 days", body: "Packed and out the door quickly." },
  ];
  return (
    <section className="border-y border-charcoal/10 bg-charcoal text-cream" data-testid="trust">
      <div className="mx-auto grid max-w-[1400px] gap-8 px-5 py-16 md:grid-cols-4 md:px-10">
        {items.map((it, i) => (
          <Reveal key={i} delay={i * 0.06}>
            <it.icon className="h-7 w-7 text-rust" strokeWidth={1.5} />
            <h3 className="mt-4 font-fraunces text-xl">{it.title}</h3>
            <p className="mt-1 font-manrope text-cream/70">{it.body}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const [products, setProducts] = useState(PRODUCTS);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee items={["Every mile, together", "For the detours", "Muddy trails & sunrises", "The long way home"]} />
      <Featured products={products} />
      <div className="mx-auto max-w-[1400px] px-5 md:px-10" data-testid="bundle-divider">
        <div className="flex items-center gap-6">
          <span className="h-px flex-1 bg-charcoal/15" />
          <span className="whitespace-nowrap font-manrope text-xs font-bold uppercase tracking-[0.2em] text-rust">
            Save more together
          </span>
          <span className="h-px flex-1 bg-charcoal/15" />
        </div>
      </div>
      <BundleSection />
      <Manifesto />
      <SafetyStory />
      <Trust />
    </div>
  );
}
